import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { getStoredUser } from '@/auth/tokenStorage'

import styles from './AuthPages.module.css'

/** Vendor / admin login page. */
export function LoginPage() {
  const { login, isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [loginValue, setLoginValue] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (isAuthenticated) {
    return <Navigate to={isAdmin ? '/admin' : '/vendor'} replace />
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await login(loginValue, password)
      const sessionUser = getStoredUser()
      navigate(sessionUser?.role === 'admin' ? '/admin' : '/vendor')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Login failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.page}>
      <div className={styles.panel}>
        <p className={styles.brand}>
          Oikos <span>Market</span>
        </p>
        <h1 className={styles.title}>Sign in</h1>
        <p className={styles.lead}>Vendors and administrators — welcome back to market day.</p>
        <form className={styles.form} onSubmit={onSubmit}>
          <label className={styles.label}>
            Username or email
            <input
              className={styles.input}
              value={loginValue}
              onChange={(e) => setLoginValue(e.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <label className={styles.label}>
            Password
            <input
              className={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
          <button className={styles.submit} type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className={styles.footer}>
          New vendor? <Link to="/signup">Create an account</Link>
          <br />
          Forgot your password? Ask an admin to send a reset link.
        </p>
      </div>
    </section>
  )
}
